import * as preact from "preact";
import * as _ from "underscore";

import * as pendingSubtitles from "pendingSubtitles";
import readlangClient from "readlangClient";
import * as transcriptUtils from "shared/transcriptUtils";
import stores from "stores/stores";

import LanguageSelectorView from "views/LanguageSelectorView";
import * as languageMap from "../../../public/src/shared/languageMap";

type Props = {
  appRouter: any;
};

interface AudioFile {
  _id: string;
  ownerID: string;
  filename: string;
  language: string;
  uploadedAt: string; // date
  whisperResponse: any;
  // Whether SRT/WebVTT subtitles were attached during upload (the transcript
  // itself stays on the server)
  hasSrtTranscript?: boolean;
  audioMap: { w: number; t: number }[];
  bookID: string;
  status:
    | "uploaded"
    | "encoding-1"
    | "encoding-2"
    | "transcribing"
    | "ready"
    | "failed";
  errorMessage: string;
  bitRate: number;
  size: number;
}

type State =
  | {
      status: "loading";
    }
  | {
      status: "loaded";
      audioFile: AudioFile;
      awaitingResponse: boolean;
      dots: number; // Number of dots to show after "Loading" from 0 to 3
      showLanguageSelector: boolean;
      quality: "medium" | "high";
      title: string;
      // SRT/WebVTT subtitles chosen on this page to use instead of automatic
      // transcription
      srtTranscript?: string;
      srtFilename?: string;
    };

class AudioUploadedPageView extends preact.Component<Props, State> {
  languageSelectorElement: HTMLElement;
  srtFileInputRef = preact.createRef<HTMLInputElement>();
  private disposeUserChange?: () => void;
  // Subtitles chosen alongside the audio file on the /upload page. They stay
  // stashed until saved or removed, so a reload of this page keeps them.
  private stashedSubtitles = pendingSubtitles.read();

  constructor(props: Props) {
    super(props);

    this.disposeUserChange = stores.user.onChange(
      (user) => [
        user.currentLearningLanguage,
        user.activeLanguages,
        user.subscriptionPlan,
      ],
      this.onUserChange
    );

    // Re-fetch user to ensure we have up-to-date data
    stores.user.fetch();

    this.state = {
      status: "loading",
    };

    this.fetchAudioFiles();

    document.addEventListener("click", this.onClickOutsideLanguageSelector);
  }

  onUserChange = () => {
    this.forceUpdate();
  };

  onSRTFileChange = (event: Event) => {
    const fileInput = event.currentTarget as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Subtitle file is too large (max 5MB).");
      fileInput.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const contents = String(reader.result ?? "");
      if (!transcriptUtils.isSRTTranscript(contents)) {
        alert(
          "This doesn't look like an SRT or WebVTT subtitle file. It should " +
            "contain timestamps like 00:00:01,000 --> 00:00:04,000"
        );
        return;
      }
      this.setState({ srtTranscript: contents, srtFilename: file.name });
    };
    reader.onerror = () => {
      alert("Failed to read subtitle file.");
    };
    reader.readAsText(file);
    fileInput.value = "";
  };

  onClickOutsideLanguageSelector = (event: MouseEvent) => {
    if (
      this.state.status === "loaded" &&
      this.state.showLanguageSelector &&
      !event.composedPath().includes(this.languageSelectorElement)
    ) {
      this.setState({ showLanguageSelector: false });
    }
  };

  componentWillUnmount() {
    this.disposeUserChange?.();
    document.removeEventListener("click", this.onClickOutsideLanguageSelector);
  }

  async fetchAudioFiles() {
    // Load audio file
    const data = (
      await readlangClient.request("GET", "/api/uploadedAudioFiles", {})
    ).data;

    this.setState({
      status: "loaded",
      audioFile: data.audioFiles[0],
      dots: this.state.status === "loaded" ? this.state.dots ?? 0 : 0,
      showLanguageSelector: false,
      quality:
        this.state.status === "loaded"
          ? this.state.quality
          : stores.user.hasPremiumPlusAccess.value
            ? "high"
            : "medium",
      title:
        this.state.status === "loaded"
          ? this.state.title
          : data.audioFiles[0].filename,
      // Only on the first load — afterwards setState merging preserves
      // whatever the user has since chosen or removed.
      ...(this.state.status === "loaded" ? {} : this.stashedSubtitles),
    });

    if (!["uploaded", "ready", "failed"].includes(data.audioFiles[0].status)) {
      setTimeout(async () => {
        if (this.state.status !== "loaded") {
          return;
        }

        await this.setState({
          audioFile: {
            ...this.state.audioFile,
          },
          dots: (this.state.dots + 1) % 4,
        });

        // Fetch every 1.5 seconds
        this.fetchAudioFiles();
      }, 1500);
    }
  }

  render() {
    if (this.state.status === "loading") {
      return <div>{this.getStatus()}</div>;
    }

    const audioFile = this.state.audioFile;

    if (audioFile === undefined) {
      return <div>No audio files uploaded</div>;
    }

    const languageString =
      languageMap.map[stores.user.currentLearningLanguage.value].english;

    // Subtitles either chosen on this page, or attached during upload
    const usingSubtitles = Boolean(
      this.state.srtTranscript || audioFile.hasSrtTranscript
    );

    // Automatic transcription only works for the languages Whisper supports,
    // so while that's the chosen path the language selector offers those
    // languages only. Supplying subtitles skips transcription entirely, so
    // then any learning language goes.
    const supportsTranscription = (languageCode: string) =>
      languageMap.map[languageCode]?.whisper === true;
    const needsSupportedLanguage =
      !usingSubtitles &&
      !supportsTranscription(stores.user.currentLearningLanguage.value);

    return (
      <div>
        <h2 style={{ margin: "30px 0 10px" }}>Transcribe and save audio</h2>
        <table>
          <tr>
            <td>Title</td>
            <td>
              {audioFile.status === "uploaded" ? (
                <input
                  type="text"
                  value={this.state.title}
                  onInput={(event) => {
                    this.setState({ title: event.currentTarget.value });
                  }}
                />
              ) : (
                this.state.title
              )}
            </td>
          </tr>
          <tr>
            <td>Language</td>
            {audioFile.status === "uploaded" ? (
              <td style={{ position: "relative" }}>
                <span
                  onClick={(event) => {
                    // Stop propagation so that it doesn't trigger the click handler on the whole
                    // document used to dismiss the dropdown
                    event.stopPropagation();
                    this.setState({ showLanguageSelector: true });
                  }}
                >
                  <select style={{ pointerEvents: "none" }}>
                    <option>{languageString}</option>
                  </select>
                </span>
                {this.state.showLanguageSelector ? (
                  <div
                    style={{
                      position: "absolute",
                      background: "white",
                      top: "0",
                      left: "0",
                      zIndex: 10,
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                    }}
                    ref={(element) => {
                      this.languageSelectorElement = element;
                    }}
                  >
                    <h3 style={{ marginBottom: "10px" }}>Select language</h3>
                    <LanguageSelectorView
                      featuredLanguages={
                        stores.user.state.value.activeLanguages
                      }
                      languageType="learning"
                      languageFilter={
                        usingSubtitles ? undefined : supportsTranscription
                      }
                      setLanguage={(languageCode: string) => {
                        this.setState({ showLanguageSelector: false });
                        stores.user.save({
                          currentLearningLanguage: languageCode,
                        });
                      }}
                    />
                  </div>
                ) : null}
                {audioFile.status === "uploaded"
                  ? " ← Make sure this is correct before continuing!"
                  : ""}
              </td>
            ) : (
              <td>{languageString}</td>
            )}
          </tr>
          {/* The quality selector mainly gates Whisper transcription, so it's
              hidden when the user supplies their own subtitles */}
          {!usingSubtitles && (
            <tr>
              <td>Audio Quality</td>
              <td>
                {audioFile.status === "uploaded" ? (
                  <select
                    onChange={(event) => {
                      const value = event.currentTarget.value as
                        | "medium"
                        | "high";

                      if (
                        value === "high" &&
                        !stores.user.hasPremiumPlusAccess.value
                      ) {
                        alert(
                          "High quality audio transcription is only available to Readlang Plus " +
                            "subscribers. Please upgrade your subscription to use this feature."
                        );
                        return;
                      }

                      this.setState({ quality: value });
                    }}
                    value={this.state.quality}
                  >
                    <option value="medium">Medium (mono)</option>
                    <option value="high">High (stereo if available)</option>
                  </select>
                ) : this.state.quality === "medium" ? (
                  "Medium (mono)"
                ) : (
                  "High (stereo if available)"
                )}
              </td>
            </tr>
          )}
          {audioFile.status === "uploaded" && (
            <tr>
              <td>Transcription</td>
              <td>
                <input
                  type="file"
                  accept=".srt,.vtt,text/plain"
                  style={{ display: "none" }}
                  ref={this.srtFileInputRef}
                  onChange={this.onSRTFileChange}
                />
                {usingSubtitles ? (
                  <>
                    Using your subtitles
                    {this.state.srtFilename &&
                      ` from ${this.state.srtFilename}`}
                    {this.state.srtTranscript && (
                      <>
                        {" "}
                        <button
                          onClick={() => {
                            pendingSubtitles.clear();
                            this.setState({
                              srtTranscript: undefined,
                              srtFilename: undefined,
                            });
                          }}
                        >
                          <i class="icon-remove"></i> Remove
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    Automatic, or{" "}
                    <button
                      data-testid="load-srt-button"
                      onClick={(event) => {
                        event.preventDefault();
                        this.srtFileInputRef.current?.click();
                      }}
                    >
                      <i class="icon-upload"></i> Load .SRT file…
                    </button>{" "}
                    to use your own subtitles
                  </>
                )}
              </td>
            </tr>
          )}
          {audioFile.status === "uploaded" ? (
            <tr>
              <td colspan={2} style={{ paddingTop: "20px" }}>
                {needsSupportedLanguage && (
                  <p class="error">
                    Automatic transcription isn't available for {languageString}
                    . Please choose a supported language above, or load your own
                    subtitles.
                  </p>
                )}
                <button
                  style={{ width: "100%" }}
                  disabled={
                    this.state.awaitingResponse || needsSupportedLanguage
                  }
                  onClick={async () => {
                    if (this.state.status !== "loaded") {
                      throw new Error("Unexpected state");
                    }
                    this.setState({
                      awaitingResponse: true,
                    });
                    await readlangClient.request("POST", "/api/processAudio", {
                      audioFileID: audioFile._id,
                      language: stores.user.currentLearningLanguage.value,
                      quality: this.state.quality,
                      title: this.state.title,
                      ...(this.state.srtTranscript
                        ? { transcript: this.state.srtTranscript }
                        : {}),
                    });
                    pendingSubtitles.clear();
                    await this.fetchAudioFiles();
                  }}
                >
                  {usingSubtitles
                    ? "Save Audio With Your Subtitles"
                    : `Generate ${languageString} Transcription`}
                </button>
              </td>
            </tr>
          ) : (
            <>
              {audioFile.bitRate && audioFile.size ? (
                <tr>
                  <td>Duration</td>
                  <td>
                    {Math.round(
                      ((audioFile.size * 8) / (audioFile.bitRate * 1000) / 60) *
                        10
                    ) / 10}{" "}
                    minutes{" "}
                  </td>
                </tr>
              ) : null}
              <tr>
                <td style={{ verticalAlign: "top" }}>
                  Transcription&nbsp;Status
                </td>
                <td>{this.getStatus()}</td>
              </tr>
              {audioFile.status === "ready" ? (
                <tr>
                  <td colSpan={2} style={{ paddingTop: "20px" }}>
                    <button
                      style={{ width: "100%" }}
                      onClick={() =>
                        this.props.appRouter.navigate(
                          `/library/${audioFile.bookID}`,
                          { trigger: true }
                        )
                      }
                    >
                      Start Listening!
                    </button>
                  </td>
                </tr>
              ) : null}
            </>
          )}
        </table>
      </div>
    );
  }

  getStatus() {
    if (this.state.status !== "loaded") {
      return <p>Loading...</p>;
    }

    switch (this.state.audioFile.status) {
      case "encoding-1":
        return "Encoding for playback (step 1 / 3)";

      case "encoding-2":
        return "Encoding for transcription (step 2 / 3)";

      case "transcribing":
        return (
          <>
            Transcribing (step 3 / 3)
            {/* {_.range(this.state.dots)
              .map(() => ".")
              .join("")} */}
            <br />
            <small>
              Please wait, transcribing takes roughly 1 minute per 10 minutes of
              audio. If you think you've waited a lot more than this, something
              might have gone wrong so please let me know at{" "}
              <a href="mailto:steve@readlang.com">steve@readlang.com</a>.
            </small>
          </>
        );

      case "ready":
        return "Transcribed";

      case "failed":
        return (
          <>
            Failed: {this.state.audioFile.errorMessage}
            <br />
            <a href="/upload">Return to upload page</a>
          </>
        );

      default:
        return this.state.audioFile.status;
    }
  }
}

export default AudioUploadedPageView;
